import { useNavigate } from 'react-router-dom'
import { Box, Typography, Button, Chip } from '@mui/material'
import { ArrowBackIos as ArrowBackIcon, KeyboardArrowDown } from '@mui/icons-material'

// others
import { BillStatus, FetchBillDetailsQuery, useIsDepartmentHeadForCaseLazyQuery, UserType } from '../../../../generated'
import { canEditBill, formatStatus, isContingencyCase, isHourlyCase, statusColors } from '../../../utils/billing'
import { BillsMenu } from '../../menus/Bills'
import { useContext, useEffect, useState } from 'react'
import { AuthContext } from '../../../context'
import { Alert } from '../../common/Alert'
import { printBillV2 } from '../../../../services'

interface BillDetailHeaderProps {
  bill?: FetchBillDetailsQuery['fetchBillDetails']['bill']
  clientId?: string
  clientPayableAmount?: number
  onRefetch: () => void
}

const BillDetailHeader = ({ bill, clientId, clientPayableAmount, onRefetch }: BillDetailHeaderProps) => {
  const [isDepartmentHead, setIsDepartmentHead] = useState<boolean | null>(null)
  const { currentUser } = useContext(AuthContext)
  const navigate = useNavigate()

  const { id, status, case: caseData } = bill || {}
  const billApprover = caseData?.billing?.billApprover
  const billing = caseData?.billing

  const isCaseParalegal =
    !!currentUser?.id &&
    !!caseData?.caseToUsers?.some(
      ctu => ctu?.userType === UserType.Paralegal && ctu?.user?.id === currentUser.id
    )

  const canEdit =
    bill?.status &&
    currentUser &&
    (canEditBill(billApprover?.id || null, isDepartmentHead, bill.status, currentUser) ||
      (isCaseParalegal &&
        (bill.status === BillStatus.Draft || bill.status === BillStatus.SentForApproval)))

  const [isDepartmentHeadForCase] = useIsDepartmentHeadForCaseLazyQuery({
    onCompleted: data => {
      if (data.isDepartmentHeadForCase.isDepartmentHead) {
        setIsDepartmentHead(data.isDepartmentHeadForCase.isDepartmentHead)
      }
    }
  })

  const handleBack = () => {
    if (!bill) return

    navigate(-1)
  }

  const handleEdit = () => {
    if (typeof id === 'number') {
      navigate(`/billing/edit-bill/${id}`)
    }
  }

  const handlePrint = async () => {
    const billId = typeof id === 'number' ? id : Number(id)
    if (!Number.isFinite(billId)) return
    try {
      await printBillV2([billId], { markAsDelivered: true })
      // Printing marks the bill as delivered; refetch so Delivery Details and the timeline show it.
      onRefetch()
    } catch (e) {
      Alert.error(e instanceof Error ? e.message : 'Failed to generate PDF')
    }
  }

  const getStatus = (status: BillStatus) => {
    if (status === BillStatus.Billed) return 'Final Bill'

    return formatStatus(status)
  }

  useEffect(() => {
    if (bill?.status === BillStatus.ApprovedByResponsibleAttorney && caseData?.id && currentUser?.id) {
      isDepartmentHeadForCase({
        variables: { fetchDepartmentHeadInput: { caseId: caseData.id, userId: currentUser.id } }
      })
    }
  }, [bill?.status, caseData?.id, currentUser?.id, isDepartmentHeadForCase])

  return (
    <Box mb={3}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Button
          startIcon={<ArrowBackIcon sx={{ width: '13px' }} />}
          sx={{
            color: '#5B5675',
            fontWeight: 600,
            fontSize: '14px',
            ml: -1
          }}
          onClick={handleBack}
        >
          BACK
        </Button>

        {bill && (
          <Box display="flex" alignItems="center" gap={2}>
            <Button variant="outlined" onClick={handlePrint}>
              PRINT
            </Button>

            <BillsMenu
              hideViewOption
              markDeliveredOnPrint
              bill={bill}
              account={bill.account}
              retainerAccount={billing?.retainerAccount}
              billingType={caseData?.billing?.billingType}
              caseId={bill.case?.id}
              billApprover={billApprover}
              clientId={clientId}
              clientPayableAmount={clientPayableAmount}
              monthlyFlatFeeAmount={billing?.monthlyFlatFee}
              isHourlyCase={billing ? isHourlyCase(billing) : false}
              isContingencyCase={billing ? isContingencyCase(billing) : false}
              onRefetch={onRefetch}
              renderAction={handleClick => (
                <Button variant="outlined" endIcon={<KeyboardArrowDown />} onClick={handleClick}>
                  Actions
                </Button>
              )}
            />

            {canEdit && (
              <Button variant="contained" onClick={handleEdit}>
                EDIT
              </Button>
            )}
          </Box>
        )}
      </Box>

      <Box display="flex" alignItems="center" gap={2} mt={1}>
        <Typography variant="h6" fontWeight={500} color="#333">
          #IN{id}
        </Typography>
        {status && (
          <Chip
            label={getStatus(status)}
            sx={{
              color: statusColors[status],
              border: `1px solid ${statusColors[status]}50`,
              backgroundColor: 'transparent'
            }}
          />
        )}
      </Box>
    </Box>
  )
}

export default BillDetailHeader
